<template>
    <div class="box">
        <div class="box-header">
            <h6 class="title-color inline">Repozytorium dokumentów</h6>
            <a class="pull-right" title="Odśwież" @click.prevent="load">
                <i class="material-icons md-18 text-blue">refresh</i>
            </a>
            <a class="pull-right m-r-sm" title="Generuj Kartę Realizacji (PDF)" @click.prevent="generateCard">
                <i class="material-icons md-18 text-blue">{{ generatingCard ? 'hourglass_empty' : 'picture_as_pdf' }}</i>
            </a>
        </div>
        <div class="m-a-0 custom-divider"/>
        <div class="box-body">
            <rotate-square2 style="margin: 15px;" v-if="loading"/>

            <template v-if="!loading">
                <!-- wFirma: dokument związany z ZO -->
                <div class="repo-group" v-if="wfirmaDoc || wfirmaFromCrm.length">
                    <div class="repo-group-title"><i class="material-icons md-14">request_quote</i> wFirma</div>
                    <div v-if="wfirmaDoc" class="repo-item">
                        <i class="material-icons md-14 repo-icon">receipt_long</i>
                        <a href="#" class="repo-link" @click.prevent="openWfirma(wfirmaDoc.id)">{{ wfirmaDoc.fullnumber || ('Dokument ' + wfirmaDoc.id) }}</a>
                        <span class="repo-tag">ZO/FV</span>
                    </div>
                    <div v-for="d in wfirmaFromCrm" :key="'wf-' + d.wFirmaId" class="repo-item">
                        <i class="material-icons md-14 repo-icon">{{ docTypeIcon(d.docType) }}</i>
                        <a v-if="d.fileKey" href="#" class="repo-link" @click.prevent="download(d.fileKey, d.fileName, d.fullnumber)">{{ d.fullnumber || '(dokument)' }}</a>
                        <span v-else class="repo-link-plain">{{ d.fullnumber || '(dokument)' }}</span>
                        <span class="repo-tag">{{ docTypeLabel(d.docType) }}</span>
                    </div>
                </div>

                <!-- Sprawy CRM + ich dokumenty -->
                <div class="repo-group" v-if="repo.crmDocuments && repo.crmDocuments.length">
                    <div class="repo-group-title"><i class="material-icons md-14">support_agent</i> Sprawy CRM</div>
                    <div v-for="t in repo.crmDocuments" :key="t.ticketId" class="repo-ticket">
                        <div class="repo-item">
                            <i class="material-icons md-14 repo-icon">confirmation_number</i>
                            <a :href="ticketHref(t.ticketId)" target="_blank" rel="noopener" class="repo-link" :title="t.subject">
                                <span class="repo-num">{{ t.ticketNumber }}</span> {{ t.subject || '(brak tematu)' }}
                            </a>
                        </div>
                        <div class="repo-subitem" v-for="o in t.wFirmaOffers" :key="'o-' + o.wFirmaId">
                            <i class="material-icons md-12 repo-icon">{{ docTypeIcon(o.docType) }}</i>
                            <a v-if="o.fileKey" href="#" class="repo-link" @click.prevent="download(o.fileKey, o.fileName, o.fullnumber)">{{ o.fullnumber || '(dokument)' }}</a>
                            <span v-else class="repo-link-plain">{{ o.fullnumber || '(dokument)' }}</span>
                            <span class="repo-tag">{{ docTypeLabel(o.docType) }}</span>
                        </div>
                        <div class="repo-subitem" v-for="(a, i) in t.attachments" :key="'a-' + t.ticketId + '-' + i">
                            <i class="material-icons md-12 repo-icon">attach_file</i>
                            <a href="#" class="repo-link" @click.prevent="download(a.fileKey, a.fileName, a.originalName)">{{ a.originalName || a.fileName }}</a>
                        </div>
                    </div>
                </div>

                <!-- Pliki zamówienia -->
                <div class="repo-group" v-if="repo.files && repo.files.length">
                    <div class="repo-group-title"><i class="material-icons md-14">folder</i> Pliki zamówienia</div>
                    <div v-for="(f, i) in repo.files" :key="'f-' + i" class="repo-item">
                        <i class="material-icons md-14 repo-icon">insert_drive_file</i>
                        <a v-if="f.fileKey" :href="getFileUrl(f.fileKey, f.fileName)" target="_blank" class="repo-link">{{ f.fileName }}</a>
                    </div>
                </div>

                <!-- Ręczne wpisy -->
                <div class="repo-group">
                    <div class="repo-group-title">
                        <i class="material-icons md-14">edit_note</i> Wpisy ręczne (telefon / SMS / mail / skan)
                        <a href="#" class="repo-add-toggle" @click.prevent="showAdd = !showAdd">
                            <i class="material-icons md-16 text-blue">{{ showAdd ? 'close' : 'add' }}</i>
                        </a>
                    </div>
                    <div v-for="m in (repo.manual || [])" :key="m._id" class="repo-item repo-manual">
                        <i class="material-icons md-14 repo-icon">{{ kindIcon(m.kind) }}</i>
                        <div class="repo-manual-body">
                            <div>
                                <span class="repo-tag">{{ kindLabel(m.kind) }}</span>
                                <strong v-if="m.title" class="repo-manual-title">{{ m.title }}</strong>
                            </div>
                            <div v-if="m.note" class="repo-manual-note">{{ m.note }}</div>
                            <a v-if="m.file && m.file.key" href="#" class="repo-link text-xs" @click.prevent="download(m.file.key, m.file.name, m.file.originalName)">
                                <i class="material-icons md-12">attach_file</i> {{ m.file.originalName || m.file.name }}
                            </a>
                            <div class="repo-manual-meta text-muted text-xs">{{ m.createdByName || '' }} · {{ m.createdAt | moment("DD/MM/YY HH:mm") }}</div>
                        </div>
                        <a class="text-grey repo-remove" href="#" @click.prevent="removeManual(m)" title="Usuń"><i class="material-icons md-16">highlight_off</i></a>
                    </div>
                    <div v-if="!(repo.manual || []).length && !showAdd" class="text-muted text-xs">Brak wpisów ręcznych</div>

                    <div v-if="showAdd" class="repo-add-form">
                        <select class="form-control input-sm" v-model="newDoc.kind">
                            <option value="phone">Telefon</option>
                            <option value="sms">SMS</option>
                            <option value="email">E-mail</option>
                            <option value="pdf">Skan / PDF</option>
                            <option value="note">Notatka</option>
                            <option value="other">Inne</option>
                        </select>
                        <input type="text" class="form-control input-sm m-t-xs" placeholder="Tytuł (np. Ustalenia telefoniczne)" v-model="newDoc.title" />
                        <textarea class="form-control input-sm m-t-xs" rows="2" placeholder="Treść / opis" v-model="newDoc.note"></textarea>
                        <div class="m-t-xs">
                            <input type="file" ref="repoFile" style="display:none;" @change="onFilePick" />
                            <a href="#" class="text-blue text-xs" @click.prevent="$refs.repoFile.click()">
                                <i class="material-icons md-14">upload_file</i> {{ newDoc.fileName ? newDoc.fileName : 'Dołącz plik (opcjonalnie)' }}
                            </a>
                            <span v-if="uploading" class="text-muted text-xs m-l-xs">Wysyłanie...</span>
                        </div>
                        <div class="m-t-xs text-right">
                            <button type="button" class="btn btn-xs white" @click="cancelAdd">Anuluj</button>
                            <button type="button" class="btn btn-xs btn-primary m-l-xs" :disabled="saving || uploading" @click="saveManual">{{ saving ? 'Zapisywanie...' : 'Dodaj' }}</button>
                        </div>
                    </div>
                </div>

                <div v-if="isEmpty" class="text-muted text-xs">Brak powiązanych dokumentów. Powiąż sprawę CRM lub dodaj wpis ręczny.</div>
            </template>
        </div>
    </div>
</template>

<script>
import Api from '../../Api.js';
import UserManager from '../../UserManager.js';
import { RotateSquare2 } from 'vue-loading-spinner';
import { generateOrderRealizationCardPdf } from '../../pdf-doc-generators/OrderRealizationCardPDF.js';

export default {
    name: 'DocumentRepository',
    props: { order: Object },
    components: { RotateSquare2 },
    data() {
        return {
            loading: false,
            // Kompozycja we froncie: files+manual z orders-service, crmDocuments z crm-service
            // (orders-service celowo nie zna CRM - patrz Ticket.relatedOrders).
            repo: { crmDocuments: [], files: [], manual: [] },
            wfirmaDoc: null,
            getFileUrl: Api.getFileUrl,
            showAdd: false,
            newDoc: { kind: 'phone', title: '', note: '', fileKey: '', fileName: '', originalName: '' },
            uploading: false,
            saving: false,
            generatingCard: false
        };
    },
    computed: {
        wfirmaFromCrm() {
            // Dokumenty wFirma zebrane ze spraw CRM (gdy nie chcemy duplikować, pokazujemy z sekcji spraw).
            return [];
        },
        isEmpty() {
            return !this.wfirmaDoc
                && !(this.repo.crmDocuments && this.repo.crmDocuments.length)
                && !(this.repo.files && this.repo.files.length)
                && !(this.repo.manual && this.repo.manual.length);
        }
    },
    created() {
        this.load();
    },
    methods: {
        async load() {
            if (!this.order || !this.order._id) return;
            this.loading = true;

            // Pliki zamówienia + wpisy ręczne (orders-service).
            try {
                const res = await Api.order.documentRepository(this.order._id);
                this.repo = {
                    crmDocuments: [],
                    files: (res && res.files) || [],
                    manual: (res && res.manual) || []
                };
            } catch (e) {
                this.repo = { crmDocuments: [], files: [], manual: [] };
            }

            // Powiązane sprawy CRM + ich dokumenty (crm-service) - tylko z uprawnieniem do CRM.
            if (UserManager.hasPermission('view.crm') || UserManager.hasPermission('imap.messages.view')) {
                try {
                    const crm = await Api.imap.tickets.byOrder(this.order._id);
                    this.repo.crmDocuments = (crm && crm.items) ? crm.items : [];
                } catch (e) { /* CRM niedostępny - sekcja pozostaje pusta */ }
            }

            try {
                const inv = await Api.wFirma.getByOrderId(this.order._id);
                this.wfirmaDoc = (inv && inv.id) ? inv : null;
            } catch (e) {
                this.wfirmaDoc = null;
            }
            this.loading = false;
        },
        docTypeIcon(t) {
            if (t === 'proforma') return 'receipt_long';
            if (t === 'stockOrder') return 'inventory_2';
            return 'request_quote';
        },
        docTypeLabel(t) {
            if (t === 'proforma') return 'PROF';
            if (t === 'stockOrder') return 'ZD';
            return 'OF';
        },
        kindIcon(k) {
            const map = { phone: 'call', sms: 'sms', email: 'mail', pdf: 'picture_as_pdf', note: 'sticky_note_2', other: 'description' };
            return map[k] || 'description';
        },
        kindLabel(k) {
            const map = { phone: 'Telefon', sms: 'SMS', email: 'E-mail', pdf: 'Skan/PDF', note: 'Notatka', other: 'Inne' };
            return map[k] || 'Inne';
        },
        download(fileKey, fileName, originalName) {
            if (!fileKey) return;
            const name = originalName || fileName || 'plik';
            Api.downloadFile(fileKey, fileName || name, name);
        },
        openWfirma(id) {
            if (!id) return;
            window.open(Api.wFirma.getFileUrl(id), '_blank');
        },
        ticketHref(id) {
            if (!id) return '#';
            try {
                const href = this.$router.resolve('/crm/tickets/' + id).href;
                return href || ('#/crm/tickets/' + id);
            } catch (e) {
                return '#/crm/tickets/' + id;
            }
        },
        async generateCard() {
            if (!this.order || !this.order._id || this.generatingCard) return;
            this.generatingCard = true;
            try {
                // Pełne dane zamówienia (z populacją i wyliczonymi kwotami) na potrzeby karty.
                let fullOrder = this.order;
                try { fullOrder = await Api.order.getOrder(this.order._id); } catch (e) { /* użyj propsa */ }

                // QR -> mobilna karta zamówienia (responsywna, dostęp tylko po zalogowaniu).
                let docUrl = '';
                try {
                    const resolved = this.$router.resolve({ name: 'order-mobile-card', params: { id: this.order._id } });
                    docUrl = window.location.origin + (resolved && resolved.href ? resolved.href : '/order/' + this.order._id + '/karta');
                } catch (e) {
                    docUrl = window.location.origin + '/order/' + this.order._id + '/karta';
                }

                const issuerName = (UserManager.user && (UserManager.user.name || UserManager.user.login)) || '';
                const blob = await generateOrderRealizationCardPdf(fullOrder, { docUrl, issuerName, cardNumber: fullOrder.name || fullOrder.code });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch (e) {
                this.$notify({ group: 'global', type: 'notification-error', text: 'Nie udało się wygenerować karty.' });
            }
            this.generatingCard = false;
        },
        onFilePick() {
            const f = this.$refs.repoFile.files[0];
            if (!f) return;
            this.uploading = true;
            const formData = new FormData();
            formData.append('file', f);
            Api.uploadFile(formData).then((ob) => {
                this.newDoc.fileKey = ob.fileKey;
                this.newDoc.fileName = ob.fileName;
                this.newDoc.originalName = f.name;
                this.uploading = false;
            }).catch(() => {
                this.uploading = false;
                this.$notify({ group: 'global', type: 'notification-error', text: 'Nie udało się wysłać pliku.' });
            });
        },
        cancelAdd() {
            this.showAdd = false;
            this.newDoc = { kind: 'phone', title: '', note: '', fileKey: '', fileName: '', originalName: '' };
        },
        async saveManual() {
            if (!this.order || !this.order._id || this.saving) return;
            if (!(this.newDoc.title || '').trim() && !(this.newDoc.note || '').trim() && !this.newDoc.fileKey) {
                this.$notify({ group: 'global', type: 'notification-error', text: 'Podaj tytuł, treść lub dołącz plik.' });
                return;
            }
            this.saving = true;
            try {
                const payload = {
                    kind: this.newDoc.kind,
                    title: this.newDoc.title,
                    note: this.newDoc.note
                };
                if (this.newDoc.fileKey) {
                    payload.file = { key: this.newDoc.fileKey, name: this.newDoc.fileName, originalName: this.newDoc.originalName };
                }
                const manual = await Api.order.addDocument(this.order._id, payload);
                this.$set(this.repo, 'manual', Array.isArray(manual) ? manual : []);
                this.cancelAdd();
                this.$notify({ group: 'global', type: 'notification-success', text: 'Dodano wpis.' });
            } catch (e) {
                this.$notify({ group: 'global', type: 'notification-error', text: 'Nie udało się dodać wpisu.' });
            }
            this.saving = false;
        },
        async removeManual(m) {
            if (!this.order || !this.order._id) return;
            if (!await this.$h.confirm('Usunąć wpis z repozytorium?')) return;
            try {
                const manual = await Api.order.removeDocument(this.order._id, m._id);
                this.$set(this.repo, 'manual', Array.isArray(manual) ? manual : []);
            } catch (e) {
                this.$notify({ group: 'global', type: 'notification-error', text: 'Nie udało się usunąć wpisu.' });
            }
        }
    }
};
</script>

<style scoped>
.repo-group { margin-bottom: 12px; }
.repo-group-title {
    display: flex; align-items: center; gap: 5px;
    font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px;
    color: #6c757d; font-weight: 600; margin-bottom: 5px;
}
.repo-add-toggle { margin-left: auto; }
.repo-item { display: flex; align-items: center; gap: 6px; padding: 3px 0; font-size: 12px; }
.repo-subitem { display: flex; align-items: center; gap: 6px; padding: 2px 0 2px 18px; font-size: 11px; }
.repo-ticket { border-left: 2px solid #e5e7eb; padding-left: 6px; margin-bottom: 6px; }
.repo-icon { color: #9ca3af; flex-shrink: 0; }
.repo-link { color: #2563eb; text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.repo-link:hover { text-decoration: underline; }
.repo-link-plain { color: #374151; }
.repo-num { font-family: monospace; font-size: 10px; color: #6b7280; }
.repo-tag { font-size: 9px; font-weight: 600; background: #eef2ff; color: #3730a3; padding: 1px 5px; border-radius: 8px; flex-shrink: 0; }
.repo-manual { align-items: flex-start; }
.repo-manual-body { flex: 1; min-width: 0; }
.repo-manual-title { font-size: 12px; margin-left: 4px; }
.repo-manual-note { font-size: 11px; color: #4b5563; white-space: pre-wrap; }
.repo-manual-meta { margin-top: 2px; }
.repo-remove { flex-shrink: 0; }
.repo-add-form { margin-top: 8px; padding: 8px; background: #f9fafb; border-radius: 6px; }
.m-t-xs { margin-top: 6px; }
.m-l-xs { margin-left: 6px; }

/* ---------- Tryb ciemny ---------- */
.theme-dark .repo-add-form {
    background: var(--dm-surface-2);
    border: 1px solid var(--dm-border);
}
.theme-dark .repo-group-title { color: var(--dm-text-muted); }
.theme-dark .repo-icon { color: var(--dm-text-muted); }
.theme-dark .repo-link { color: var(--dm-link); }
.theme-dark .repo-link:hover { color: var(--dm-link-hover); }
.theme-dark .repo-link-plain { color: var(--dm-text); }
.theme-dark .repo-num { color: var(--dm-text-muted); }
.theme-dark .repo-manual-note { color: var(--dm-text-muted); }
.theme-dark .repo-manual-title { color: var(--dm-text); }
.theme-dark .repo-ticket { border-left-color: var(--dm-border); }
.theme-dark .repo-tag {
    background: var(--dm-tint-blue);
    color: var(--dm-on-blue);
}
</style>
